import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@tcg/ui";
import { CheckCircle2, FileText, Repeat, TriangleAlert, Undo2, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";

import { ApprovalCancelled, useApproval } from "@/app/approval";
import { usePos } from "@/app/context";
import { MoneyText } from "@/components/MoneyText";
import { useCart } from "@/features/sale/hooks/useCart";
import { call } from "@/lib/api";
import { errorText, isApiError } from "@/lib/errors";
import { newIdempotencyKey } from "@/lib/idempotency";
import { printReturnSlip } from "@/lib/receipts";
import type { ReturnCreate, ReturnResult, SaleLookup } from "@/lib/types";

import { OrderLookup } from "./OrderLookup";
import { RefundMethods, refundsOf, type RefundChoice } from "./RefundMethods";
import { ReturnLines, type LineChoice } from "./ReturnLines";
import { VoidDialog } from "./VoidDialog";

const METHOD_KEY: Record<string, string> = {
  cash: "tender.method_cash",
  edc_external: "tender.method_card",
  store_credit: "tender.method_credit",
};

function when(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale === "ja" ? "ja-JP" : locale === "th" ? "th-TH" : "en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tokyo",
  }).format(new Date(value));
}

/**
 * 返品 (T07-27, design/05 §3.6): find the sale → tick what comes back (reason + back on sale /
 * damaged) → the refund the server calculated (tax-service), by default the way it was paid →
 * 「返金する」 (the manager's PIN when needed) → the 返品 slip prints, the drawer opens for cash,
 * the 返還インボイス follows (≥ ¥10,000). 取消 for a sale of the open shift; 交換 opens a new
 * basket after the return. ≤ 5 taps after the scan.
 */
export function ReturnScreen() {
  const { t } = useTranslation();
  const { api } = usePos();
  const [params, setParams] = useSearchParams();
  const [sale, setSale] = useState<SaleLookup | null>(null);
  const [finding, setFinding] = useState(false);
  const [findError, setFindError] = useState<string | null>(null);
  const [result, setResult] = useState<ReturnResult | null>(null);

  const find = useCallback(
    async (code: string) => {
      setFinding(true);
      setFindError(null);
      try {
        const found = await call(() =>
          api.GET("/v1/pos/orders/lookup", { params: { query: { code } } }),
        );
        setSale(found);
      } catch (err) {
        setFindError(errorText(err, t));
      } finally {
        setFinding(false);
      }
    },
    [api, t],
  );

  // opened from a receipt (取引履歴 → 返品): the sale is already known
  const fromReceipt = params.get("sale");
  const opened = useRef(false);
  useEffect(() => {
    if (fromReceipt && !opened.current) {
      opened.current = true;
      void find(fromReceipt);
    }
  }, [find, fromReceipt]);

  const restart = () => {
    setSale(null);
    setResult(null);
    setFindError(null);
    if (params.has("sale")) {
      setParams({}, { replace: true });
    }
  };

  if (result) {
    return <ReturnDone result={result} onNext={restart} />;
  }
  if (!sale) {
    return <OrderLookup onCode={(code) => void find(code)} busy={finding} error={findError} />;
  }
  return <ReturnBill sale={sale} onBack={restart} onDone={setResult} />;
}

// ---- the bill: lines + refund ----------------------------------------------------------------

function ReturnBill({
  sale,
  onBack,
  onDone,
}: {
  sale: SaleLookup;
  onBack: () => void;
  onDone: (result: ReturnResult) => void;
}) {
  const { t, i18n } = useTranslation();
  const { api } = usePos();
  const cart = useCart();
  const { withApproval } = useApproval();
  const [chosen, setChosen] = useState<ReadonlyMap<string, LineChoice>>(() => new Map());
  const [choice, setChoice] = useState<RefundChoice>("original");
  const [edcRefs, setEdcRefs] = useState<Record<number, string>>({});
  const [exchange, setExchange] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = useRef(newIdempotencyKey());
  const locale = i18n.resolvedLanguage ?? "ja";

  const picked = useMemo(
    () =>
      [...chosen.entries()]
        .map(([lineId, c]) => ({ lineId, qty: c.qty }))
        .sort((a, b) => a.lineId.localeCompare(b.lineId)),
    [chosen],
  );
  const preview = useQuery({
    queryKey: ["pos-returns", "preview", sale.saleId, picked],
    queryFn: () =>
      call(() =>
        api.POST("/v1/pos/returns/preview", { body: { saleId: sale.saleId, lines: picked } }),
      ),
    enabled: picked.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const fresh =
    picked.length > 0 && preview.data && !preview.isPlaceholderData && !preview.isFetching;
  const finalSale = Boolean(
    (preview.data?.finalSaleLineIds ?? []).some((id) => chosen.has(id)) && picked.length > 0,
  );

  const change = (lineId: string, next: LineChoice | null) => {
    setChosen((current) => {
      const copy = new Map(current);
      if (next) {
        copy.set(lineId, next);
      } else {
        copy.delete(lineId);
      }
      return copy;
    });
    setChoice("original");
    setEdcRefs({});
  };

  const submit = async () => {
    if (!fresh || !preview.data || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let exchangeCartId: string | null = null;
      if (exchange) {
        exchangeCartId =
          cart.cartId && (cart.cart?.lines.length ?? 0) === 0
            ? cart.cartId
            : (await call(() => api.POST("/v1/pos/carts", { body: {} }))).id;
      }
      const body: ReturnCreate = {
        saleId: sale.saleId,
        lines: [...chosen.entries()].map(([lineId, c]) => ({
          lineId,
          qty: c.qty,
          reason: c.reason,
          inspection: c.inspection,
        })),
        refunds: refundsOf(preview.data, choice, edcRefs, sale.customerId),
        finalSaleOverride: finalSale,
        ...(exchangeCartId ? { exchangeCartId } : {}),
      };
      const answer = await withApproval(
        (token) =>
          call(() =>
            api.POST("/v1/pos/returns", {
              body,
              headers: {
                "Idempotency-Key": key.current,
                ...(token ? { "X-Approval-Token": token } : {}),
              },
            }),
          ),
        { subject: sale.saleId, amount: preview.data.total },
      );
      onDone(answer);
    } catch (err) {
      if (!isApiError(err, "network") && !(isApiError(err) && err.status >= 500)) {
        key.current = newIdempotencyKey();
      }
      if (err instanceof ApprovalCancelled) {
        setError(t("approval.cancelled"));
      } else {
        if (isApiError(err, "refund_total_mismatch")) {
          void preview.refetch();
        }
        setError(errorText(err, t));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-[minmax(0,1fr)_26rem] gap-4 p-4">
      <section aria-labelledby="return-bill" className="flex min-h-0 flex-col gap-3">
        <header className="flex flex-wrap items-center gap-3">
          <h1 id="return-bill" className="text-2xl font-semibold">
            {t("returns.bill_title", { no: sale.receiptNo ?? sale.orderNumber })}
          </h1>
          <span className="text-muted-foreground">{when(sale.completedAt, locale)}</span>
          {sale.taxFree ? <Badge variant="pending">{t("returns.tax_free_sale")}</Badge> : null}
          <span className="ml-auto flex gap-2">
            {sale.voidable ? (
              <button
                type="button"
                onClick={() => setVoiding(true)}
                className="flex min-h-pos-touch items-center gap-2 rounded-md border border-status-problem px-4 text-lg text-pos-problem"
              >
                <XCircle aria-hidden />
                {t("returns.void")}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onBack}
              className="min-h-pos-touch rounded-md border px-4 text-lg"
            >
              {t("returns.other_sale")}
            </button>
          </span>
        </header>
        <p className="text-sm text-muted-foreground">
          {t("returns.paid_with")}{" "}
          {sale.payments.map((p, i) => (
            <span key={p.paymentId ?? `${p.method}-${String(i)}`} data-testid="paid-with">
              {i > 0 ? " + " : ""}
              {t(METHOD_KEY[p.method] ?? "tender.method_card")}
              {/* the cap is payment-service's refundable (T06-23 / T07-42) — never computed here */}
              {p.refundable < p.amount ? (
                <>
                  {" "}
                  ({t("returns.refundable_left")} <MoneyText amount={p.refundable} size="sm" />)
                </>
              ) : null}
            </span>
          ))}
          {" · "}
          {t("returns.sale_total")} <MoneyText amount={sale.total} size="sm" />
        </p>
        <div className="min-h-0 overflow-y-auto">
          <ReturnLines lines={sale.lines} chosen={chosen} onChange={change} />
        </div>
      </section>
      <aside className="flex min-h-0 flex-col gap-3 overflow-y-auto rounded-md border p-4">
        {picked.length === 0 ? (
          <p className="text-lg text-muted-foreground">{t("returns.pick_first")}</p>
        ) : preview.isError ? (
          <p role="alert" className="text-pos-problem">
            {errorText(preview.error, t)}
          </p>
        ) : preview.data ? (
          <RefundMethods
            preview={preview.data}
            choice={choice}
            onChoice={setChoice}
            hasCustomer={Boolean(sale.customerId)}
            edcRefs={edcRefs}
            onEdcRef={(i, value) => setEdcRefs({ ...edcRefs, [i]: value })}
          />
        ) : (
          <p aria-live="polite">{t("app.loading")}</p>
        )}
        {finalSale ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border-2 border-status-problem p-3"
          >
            <TriangleAlert aria-hidden className="size-6 shrink-0 text-pos-problem" />
            {t("returns.final_sale_warning")}
          </p>
        ) : null}
        {picked.length > 0 ? (
          <label className="flex min-h-pos-touch items-center gap-3 rounded-md border px-3 text-lg">
            <input
              type="checkbox"
              checked={exchange}
              onChange={(e) => setExchange(e.target.checked)}
              className="size-6"
            />
            <Repeat aria-hidden className="size-5" />
            {t("returns.exchange")}
          </label>
        ) : null}
        {error ? (
          <p role="alert" className="text-pos-problem">
            {error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!fresh || busy}
          className="mt-auto flex min-h-20 items-center justify-center gap-2 rounded-lg bg-primary text-2xl font-bold text-primary-foreground disabled:opacity-40"
        >
          <Undo2 aria-hidden className="size-7" />
          {busy ? t("app.saving") : t("returns.submit")}
        </button>
      </aside>
      {voiding ? (
        <VoidDialog sale={sale} onClose={() => setVoiding(false)} onVoided={onDone} />
      ) : null}
    </div>
  );
}

// ---- after the return ------------------------------------------------------------------------

/** Answers whose slip already printed (StrictMode runs effects twice on the same answer). */
const printed = new WeakSet<ReturnResult>();

function ReturnDone({ result, onNext }: { result: ReturnResult; onNext: () => void }) {
  const { t, i18n } = useTranslation();
  const { api, printer } = usePos();
  const cart = useCart();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [print, setPrint] = useState<"printing" | "printed" | "failed">("printing");
  const locale = (i18n.resolvedLanguage ?? "ja") as "ja" | "th" | "en";

  useEffect(() => {
    if (printed.has(result)) {
      return;
    }
    printed.add(result);
    void (async () => {
      if (result.openDrawer && printer.config.drawer) {
        await printer.queue.openDrawer();
      }
      const outcome = await printReturnSlip({
        api,
        printer,
        returnId: result.id,
        label: result.receiptNo,
      });
      setPrint(outcome.job?.status === "done" ? "printed" : "failed");
    })();
    void queryClient.invalidateQueries({ queryKey: ["pos-sales"] });
  }, [api, printer, queryClient, result]);

  const henkan = useQuery({
    queryKey: ["pos-returns", "detail", result.id, "henkan"],
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/returns/{return_id}/henkan-invoice", {
          params: { path: { return_id: result.id } },
        }),
      ),
    enabled: Boolean(result.henkanInvoice.required),
    // made by media-service in the background — shows up by itself (no refresh button)
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 2_000 : false),
  });

  const sellNew = async () => {
    if (result.exchangeCartId) {
      await cart.openCart(result.exchangeCartId);
    }
    void navigate("/");
  };

  return (
    <section
      aria-labelledby="return-done"
      className="grid h-full grid-cols-[minmax(0,1fr)_22rem] gap-6 p-8"
    >
      <div className="flex flex-col gap-4">
        <h1 id="return-done" className="flex items-center gap-3 text-3xl font-semibold text-pos-ok">
          <CheckCircle2 aria-hidden className="size-10" />
          {result.kind === "void" ? t("returns.voided_title") : t("returns.done_title")}
        </h1>
        <p className="text-lg">
          {t("returns.slip_no", { no: result.receiptNo })}
          {result.originalReceiptNo
            ? ` · ${t("returns.original_no", { no: result.originalReceiptNo })}`
            : ""}
        </p>
        <ul className="flex flex-col gap-2" data-testid="refunds-done">
          {result.refunds.map((r, i) => (
            <li key={`${r.method}-${String(i)}`} className="flex flex-col rounded-md border p-3">
              <span className="flex items-baseline justify-between text-xl">
                {t(METHOD_KEY[r.method] ?? "tender.method_card")}
                <MoneyText amount={r.amount} size="lg" />
              </span>
              {r.edcVoidRequired ? (
                <span className="text-pos-problem">{t("returns.edc_do_it")}</span>
              ) : null}
            </li>
          ))}
        </ul>
        {result.messages?.map((m) => (
          <p key={m.code} role="status" className="rounded-md border border-status-pending p-2">
            {m[locale] ?? m.ja ?? m.en}
          </p>
        ))}
        <p aria-live="polite">
          {print === "printing"
            ? t("done.printing")
            : print === "printed"
              ? t("returns.slip_printed")
              : t("done.print_failed")}
        </p>
        {result.henkanInvoice.required ? (
          <div className="flex items-center gap-3 rounded-md border p-3" data-testid="henkan">
            <FileText aria-hidden className="size-6" />
            <span className="flex-1">{t("returns.henkan_title")}</span>
            {henkan.data?.status === "issued" && henkan.data.url ? (
              <a
                href={henkan.data.url}
                target="_blank"
                rel="noreferrer"
                className="min-h-pos-touch rounded-md bg-primary px-4 py-3 text-lg font-semibold text-primary-foreground"
              >
                {t("returns.henkan_open")}
              </a>
            ) : (
              <span className="text-muted-foreground">
                {henkan.data?.status === "unavailable"
                  ? t("returns.henkan_unavailable")
                  : t("returns.henkan_pending")}
              </span>
            )}
          </div>
        ) : null}
      </div>
      <aside className="flex flex-col justify-center gap-3">
        {result.exchangeCartId ? (
          <button
            type="button"
            autoFocus
            onClick={() => void sellNew()}
            className="min-h-24 rounded-lg bg-primary text-2xl font-bold text-primary-foreground"
          >
            {t("returns.exchange_sell")}
          </button>
        ) : null}
        <button
          type="button"
          autoFocus={!result.exchangeCartId}
          onClick={onNext}
          className={
            result.exchangeCartId
              ? "min-h-pos-touch rounded-md border text-lg"
              : "min-h-24 rounded-lg bg-primary text-2xl font-bold text-primary-foreground"
          }
        >
          {t("returns.next")}
        </button>
      </aside>
    </section>
  );
}
